import { Theme } from '@radix-ui/themes'
import { Alert, List, message, Skeleton, Tag, Typography } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import {
  createClassificationIndicationConfigContextValue,
  DEFAULT_CLASSIFICATION_CONFIG,
  IndicationClassificationConfigContext,
  IndicationItem,
  indicationToEntry,
  RolodexIndication,
  RolodexIndicationContextProvider,
  ToggledIndicationDetailView,
  UpdateIndicationContext,
} from 'features/indicationTriage'
import { FfsRegionClassification } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionClassificationSchema'
import { entryRouteToPartLevel } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionRouting'
import { FfsPart } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { useClassifiedLtas } from 'features/libra/stepComponents/ffs/classifiedLtas'
import {
  computeFfsPartValidation,
  hasRecordedAssessment,
} from 'features/libra/stepComponents/ffs/computeFfsPartValidation'
import {
  DEFAULT_FFS_LTA_DETAIL_CARDS,
  normalizeFfsDetailCards,
  normalizeFfsDetailCardsForPart,
} from 'features/libra/stepComponents/ffs/ffsDetailCards'
import { FFS_INDICATION_LAYER_PREFIX } from 'features/libra/stepComponents/ffs/ffsIndicationMarkup'
import { FfsReportRecord } from 'features/libra/stepComponents/ffs/ffsReportRecord'
import { useFfsMarkupLayers } from 'features/libra/stepComponents/ffs/useFfsMarkupLayers'
import { useFfsPartReports } from 'features/libra/stepComponents/ffs/useFfsPartReports'
import { useFfsTicketContext } from 'features/libra/stepComponents/ffs/useFfsTicketContext'
import { useFfsTriageData } from 'features/libra/stepComponents/ffs/useFfsTriageData'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { useFfsWorkspaceState } from 'features/libra/workspace/ffs/FfsWorkspaceStateContext'
import { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

/**
 * A COARSE per-LTA status for the list row. Deliberately NOT the pass/fail verdict
 * (that lives in the calc card / close-out): it reflects whether the LTA has a
 * recorded assessment via ANY eligible cell — the same holistic gate that drives
 * step completion — so the row can never read "not started" inside a green step.
 *
 * A Type-C LTA (not assessable anywhere) shows `n/a`; an unclassified LTA is
 * permissive with an unknown route (`optional`).
 */
const ltaPartStatus = (
  classification: FfsRegionClassification | null,
  reports: ReadonlyArray<FfsReportRecord>,
  parseFailures: number
): { label: string; color?: string } => {
  // Unclassified: permissive, route unknown.
  if (!classification) return { label: 'optional' }
  // Type C is not assessable anywhere.
  if (entryRouteToPartLevel(classification.entryRoute) === null) {
    return { label: 'n/a' }
  }
  return hasRecordedAssessment(classification, reports, parseFailures)
    ? { label: 'assessed', color: 'blue' }
    : { label: 'not started' }
}

const PART_META: Record<FfsPart, { title: string; blurb: string }> = {
  [FfsPart.PART_4]: {
    title: 'FFS Part 4 — General Corrosion',
    blurb:
      'Part 4 (general metal loss) is the screening assessment every LTA can start with. Run whichever API-579-eligible level or part is the appropriate method — any passing assessment counts.',
  },
  [FfsPart.PART_5]: {
    title: 'FFS Part 5 — Local Metal Loss',
    blurb:
      'Part 5 (local metal loss) is the detailed assessment for local LTAs. Run whichever API-579-eligible level or part is the appropriate method — any passing assessment counts.',
  },
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

interface FfsPartStepProps {
  ffsPart: FfsPart
  readonly?: boolean
}

/**
 * Shared core for the part-scoped FFS steps (Part 4 / Part 5). Lists the LTA
 * indications and opens the inline `ToggledIndicationDetailView`, whose FFS
 * calc card is PINNED to `ffsPart` (via `normalizeFfsDetailCardsForPart`) so it
 * shows only that part's Level 1 / Level 2 (with per-LTA level locking).
 *
 * Split out of the former combined triage step. The manual classify buttons
 * are gone: classification is derived from the governing FFS report.
 */
export const FfsPartStep: FC<FfsPartStepProps> = ({
  ffsPart,
  readonly: readOnly = false,
}) => {
  const {
    component,
    componentId,
    inspectionId,
    assetModel,
    isLoading: ticketLoading,
  } = useFfsTicketContext()
  const {
    ltaSet,
    indications,
    isLoading: triageDataLoading,
    forceRefresh: refreshTriageIndications,
  } = useFfsTriageData({ component, inspectionId })

  // Each LTA carries its own committed region classification (from FFS
  // Pre-Planning), so eligibility/scope is per-LTA. Parsed ONCE here and shared
  // by the step validator and the detail-card config below.
  const classifiedLtas = useClassifiedLtas(indications)

  const indicationIds = useMemo(
    () => indications.map((indication) => indication.id),
    [indications]
  )
  // antd `List` wants a mutable `T[]`; memoize one copy of the stable
  // ReadonlyArray instead of spreading a fresh array every render.
  const dataSource = useMemo(() => [...indications], [indications])
  const {
    reportsByIndication,
    parseFailuresByIndication,
    isLoading: partReportsLoading,
    forceRefresh: refreshPartReports,
  } = useFfsPartReports(indicationIds)

  // Per-indication row status, computed ONCE per data change — NOT per row per
  // render. `ltaPartStatus` runs the full governing selection, so calling it
  // inline in `renderItem` re-ran it for every LTA on every render (and again on
  // each row click). Precompute a lookup map instead.
  const statusById = useMemo(() => {
    const map = new Map<string, { label: string; color?: string }>()
    for (const { indication, classification } of classifiedLtas) {
      map.set(
        indication.id,
        ltaPartStatus(
          classification,
          reportsByIndication.get(indication.id) ?? [],
          parseFailuresByIndication.get(indication.id) ?? 0
        )
      )
    }
    return map
  }, [classifiedLtas, reportsByIndication, parseFailuresByIndication])

  const isLoading = ticketLoading || triageDataLoading || partReportsLoading

  const {
    validation,
    eligibleLtaCount,
    assessedLtaCount,
    unclassifiedLtaCount,
  } = useMemo(
    () =>
      computeFfsPartValidation({
        part: ffsPart,
        componentId,
        inspectionId,
        component,
        ltaSet,
        classifiedLtas,
        reportsByIndication,
        parseFailuresByIndication,
        isLoading,
      }),
    [
      ffsPart,
      component,
      componentId,
      inspectionId,
      ltaSet,
      classifiedLtas,
      reportsByIndication,
      parseFailuresByIndication,
      isLoading,
    ]
  )

  useReportValidation(validation)

  const [messageApi, messageContextHolder] = message.useMessage()
  const { rolodexPut } = useRolodexEdit()
  const {
    selectedIndicationId,
    setSelectedIndicationId,
    primaryCdf,
    setZoomToLayerId,
  } = useFfsWorkspaceState()

  useFfsMarkupLayers({
    indications,
    ltaSetId: ltaSet?.id,
    component,
    inspectionId,
    selectedIndicationId,
  })

  const [detailIndication, setDetailIndication] =
    useState<RolodexIndication | null>(null)

  // Detail view <-> viz selection sync (same contract as the old triage step).
  useEffect(() => {
    if (!selectedIndicationId) {
      if (detailIndication) setDetailIndication(null)
      return
    }
    if (detailIndication?.id === selectedIndicationId) return
    const match = indications.find((i) => i.id === selectedIndicationId)
    if (match) setDetailIndication(match)
  }, [selectedIndicationId, indications, detailIndication])

  const classificationConfigValue = useMemo(
    () =>
      createClassificationIndicationConfigContextValue(
        DEFAULT_CLASSIFICATION_CONFIG
      ),
    []
  )

  const updateContextValue = useMemo(
    () => ({
      updateOrAddIndication: (item: IndicationItem) => {
        // On a read-only ticket refuse drawer-driven writes.
        if (readOnly) {
          messageApi.warning(
            'Ticket is read-only; changes were not saved to Rolodex.'
          )
          return
        }
        const rolodexItem = item as RolodexIndication
        const baseEntry = indicationToEntry(rolodexItem)
        const classificationInfo = rolodexItem.classificationInfo
          ? {
              classification: rolodexItem.classificationInfo.classification,
              refVal: rolodexItem.classificationInfo.refVal?.num,
              unitId: rolodexItem.classificationInfo.refVal?.unit.id,
            }
          : undefined
        void (async () => {
          try {
            await rolodexPut([
              {
                ...baseEntry,
                data: {
                  ...baseEntry.data,
                  ...(classificationInfo ? { classificationInfo } : {}),
                },
              },
            ])
            await refreshTriageIndications()
            await refreshPartReports()
          } catch {
            messageApi.error(`Failed to update ${rolodexItem.id}`)
          }
        })()
      },
      // Refresh BOTH the indication query and this step's part-completion query
      // -- report saves in the calc card call this, and completion must react.
      refreshIndications: async () => {
        await refreshTriageIndications()
        await refreshPartReports()
      },
    }),
    [
      messageApi,
      readOnly,
      refreshTriageIndications,
      refreshPartReports,
      rolodexPut,
    ]
  )

  const resolvedDetailCards = useMemo(() => {
    const base = normalizeFfsDetailCards(
      ltaSet?.detailCards?.length
        ? ltaSet.detailCards
        : DEFAULT_FFS_LTA_DETAIL_CARDS
    )
    // The calc card enforces the LTA's selected route from this classification
    // (see FfsCalcCard). Read from the shared single parse; the fail-closed
    // `null` is passed through as-is -- do NOT launder it into `undefined`,
    // which the card would read as "no decision to enforce".
    const selected = classifiedLtas.find(
      (l) => l.indication.id === detailIndication?.id
    )
    return normalizeFfsDetailCardsForPart(
      base,
      ffsPart,
      selected?.classification ?? null
    )
  }, [ltaSet?.detailCards, ffsPart, detailIndication?.id, classifiedLtas])

  const detailExtensionContext = useMemo(
    () => ({
      bottomPanelContainer: null,
      requestAuxiliaryBottomPanel: () => {},
      releaseAuxiliaryBottomPanel: () => {},
      zoomToRegion: (regionId: string) => setZoomToLayerId(regionId),
    }),
    [setZoomToLayerId]
  )

  const meta = PART_META[ffsPart]

  if (isLoading) {
    return (
      <Container>
        <Skeleton active />
      </Container>
    )
  }

  return (
    <Container>
      {messageContextHolder}
      <Typography.Title level={4}>{meta.title}</Typography.Title>
      <Typography.Paragraph type='secondary'>
        {meta.blurb} {assessedLtaCount} of {eligibleLtaCount} assessed.
      </Typography.Paragraph>

      {!componentId || !inspectionId ? (
        <Alert
          type='error'
          showIcon
          message='Ticket is missing required component or inspection tag.'
        />
      ) : !component ? (
        <Alert
          type='error'
          showIcon
          message='Component entry not found in Rolodex.'
        />
      ) : !ltaSet ? (
        <Alert
          type='warning'
          showIcon
          message='No LTA indication set found yet. Return to the LTA Generation step and generate Local Thin Areas first.'
        />
      ) : indications.length === 0 ? (
        <Alert
          type='info'
          showIcon
          message={`Indication set "${ltaSet.id}" exists but has no indications.`}
        />
      ) : (
        <>
          {unclassifiedLtaCount > 0 ? (
            <Alert
              type='warning'
              showIcon
              style={{ marginBottom: '0.5rem' }}
              message={`${unclassifiedLtaCount} LTA${unclassifiedLtaCount === 1 ? ' is' : 's are'} missing or invalid FFS Pre-Planning classification. ${unclassifiedLtaCount === 1 ? 'It is' : 'They are'} shown below and optional until classified — you can assess ${unclassifiedLtaCount === 1 ? 'it' : 'them'} without a validated route.`}
            />
          ) : null}
          <List
            size='small'
            bordered
            dataSource={dataSource}
            renderItem={(indication: RolodexIndication) => {
              const status = statusById.get(indication.id) ?? {
                label: 'not started',
              }
              return (
                <List.Item
                  onClick={() => {
                    setSelectedIndicationId(indication.id)
                    setDetailIndication(indication)
                    // Frame the viz on this LTA so the engineer doesn't have to
                    // hunt for it. The one-shot effect in FfsAssetVizPanel resolves
                    // the prefixed layer id to bounds and zooms, then clears it.
                    setZoomToLayerId(
                      `${FFS_INDICATION_LAYER_PREFIX}${indication.id}`
                    )
                  }}
                  style={{ cursor: 'pointer' }}>
                  <Typography.Link>{indication.title}</Typography.Link>
                  <Tag color={status.color}>{status.label}</Tag>
                </List.Item>
              )
            }}
          />
        </>
      )}

      <Theme asChild hasBackground={false} appearance='inherit'>
        <IndicationClassificationConfigContext.Provider
          value={classificationConfigValue}>
          <UpdateIndicationContext.Provider value={updateContextValue}>
            {detailIndication !== null ? (
              <RolodexIndicationContextProvider
                indicationId={detailIndication.id}
                resolvedThicknessCdf={primaryCdf}
                resolvedAssetModel={assetModel}>
                <ToggledIndicationDetailView
                  isOpen={detailIndication !== null}
                  close={() => {
                    setDetailIndication(null)
                    setSelectedIndicationId(null)
                  }}
                  indicationItem={detailIndication}
                  cardConfig={resolvedDetailCards}
                  extensionContext={detailExtensionContext}
                  type='inline'
                />
              </RolodexIndicationContextProvider>
            ) : null}
          </UpdateIndicationContext.Provider>
        </IndicationClassificationConfigContext.Provider>
      </Theme>
    </Container>
  )
}
